import { useCallback, useEffect, useRef, useState } from 'react'

interface Options {
    /** When false nothing is fetched (and polling is paused). */
    enabled?: boolean
    /** Poll interval in ms; 0/undefined disables polling. */
    pollMs?: number
    /** Use the silent (no global loader) request for the load triggered by a changed fetcher. */
    silentLoads?: boolean
}

/**
 * Fetch hook with the AuditLogs request-id race guard, plus optional polling that never overlaps
 * (a tick is skipped while a request is in flight) and is cleared on unmount.
 *
 * `fetcher` must be memoised by the caller (useCallback): a new identity triggers a reload.
 */
export function useGuardedFetch<T>(fetcher: (silent: boolean) => Promise<T>, options: Options = {}) {
    const { enabled = true, pollMs = 0, silentLoads = false } = options
    const [data, setData] = useState<T | null>(null)
    const [error, setError] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const reqRef = useRef(0)
    const inFlight = useRef(false)
    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    const run = useCallback(async (mode: 'initial' | 'poll' | 'refresh', silentInitial: boolean) => {
        if (mode === 'poll' && inFlight.current) return // never overlap polling requests
        const rid = ++reqRef.current // supersedes any older in-flight request
        inFlight.current = true
        if (mode === 'initial') setLoading(true)
        try {
            const res = await fetcherRef.current(mode === 'initial' ? silentInitial : true)
            if (rid !== reqRef.current) return
            setData(res)
            setError(null)
        } catch (e) {
            if (rid !== reqRef.current) return
            setError(e)
        } finally {
            if (rid === reqRef.current) {
                inFlight.current = false
                setLoading(false)
            }
        }
    }, [])

    // ignore anything still in flight after unmount / param change
    const invalidate = useCallback(() => {
        reqRef.current += 1
        inFlight.current = false
    }, [])

    useEffect(() => {
        if (!enabled) return
        inFlight.current = false
        run('initial', silentLoads)
        return invalidate
    }, [fetcher, enabled, silentLoads, run, invalidate])

    useEffect(() => {
        if (!enabled || !pollMs) return
        const t = setInterval(() => { run('poll', true) }, pollMs)
        return () => clearInterval(t)
    }, [enabled, pollMs, run])

    const refetch = useCallback(() => run('refresh', true), [run])
    return { data, error, loading, refetch, setData }
}
